import {TodoStatus} from '@cp/model';
import {Box, Checkbox, Chip, IconButton, ListItem, ListItemText, Menu, MenuItem, Typography} from '@mui/material';
import {MoreVert, Edit, Delete, Archive, Unarchive} from '@mui/icons-material';
import {useState} from 'react';
import {Timestamp} from 'firebase/firestore';
import {TodoWithExpiration} from '../../hooks/todo/useTodos';
import useUpdateTodo from '../../hooks/todo/useUpdateTodo';
import useDeleteTodo from '../../hooks/todo/useDeleteTodo';

interface TodoListItemProps {
  todo: TodoWithExpiration;
  onEdit: () => void;
  uid?: string;
}

export default function TodoListItem({todo, onEdit, uid}: TodoListItemProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {updateTodo} = useUpdateTodo(uid);
  const {deleteTodo} = useDeleteTodo(uid);

  const handleToggleDone = async () => {
    try {
      if (todo.done) {
        await updateTodo(todo.id, {done: null});
      } else {
        await updateTodo(todo.id, {done: Timestamp.now()});
      }
    } catch (err) {
      console.error('Failed to toggle done:', err);
    }
  };

  const handleArchive = async () => {
    try {
      await updateTodo(todo.id, {status: todo.status === TodoStatus.ARCHIVE ? TodoStatus.ACTIVE : TodoStatus.ARCHIVE});
      setAnchorEl(null);
    } catch (err) {
      console.error('Failed to archive:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this todo?')) return;

    try {
      await deleteTodo(todo.id);
      setAnchorEl(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const isDone = todo.done !== null;
  const opacity = todo.isExpired && isDone ? 0.5 : 1;

  return (
    <ListItem
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        opacity,
      }}
    >
      <Checkbox checked={isDone} onChange={handleToggleDone} sx={{opacity}} />
      <ListItemText
        primary={
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography variant="body1" sx={{fontWeight: isDone ? 'normal' : 500}}>
              {todo.title}
            </Typography>
            {todo.tags.length > 0 && (
              <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                {todo.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Box>
            )}
            {todo.repeat &&
              (todo.repeat.unit === 'dayOfWeek' && todo.repeat.daysOfWeek && todo.repeat.daysOfWeek.length > 0 ? (
                <Chip
                  label={`Repeat: ${todo.repeat.daysOfWeek
                    .sort((a, b) => a - b)
                    .map((d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
                    .join(', ')}`}
                  size="small"
                  color="secondary"
                />
              ) : (
                <Chip label={`Repeat: ${todo.repeat.number} ${todo.repeat.unit}(s)`} size="small" color="secondary" />
              ))}
          </Box>
        }
        secondary={todo.note}
      />
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={onEdit}>
          <Edit sx={{mr: 1}} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          {todo.status === 'archive' ? (
            <>
              <Unarchive sx={{mr: 1}} />
              Unarchive
            </>
          ) : (
            <>
              <Archive sx={{mr: 1}} />
              Archive
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete sx={{mr: 1}} />
          Delete
        </MenuItem>
      </Menu>
    </ListItem>
  );
}
